import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import ExportCsvButton from "components/UI/ExportCsvButton";
import { returnUpDownClassname } from "utils/helpers";

const OptionsSignalTable = (props) => {
  const { signals } = props;

  const sortedSignals = useMemo(
    () =>
      signals
        .slice()
        .sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate)),
    [signals]
  );

  const sortByNumber = useCallback((a, b) => {
    if (a === b) return 0;
    if (a === null) return -1;
    if (b === null) return 1;
    return a > b ? 1 : -1;
  }, []);

  const columns = [
    {
      title: "Entry Date",
      dataIndex: "entryDate",
      render: (text) => (
        <div className="flex flex-col gap-1">
          <p className="text-xs">{dayjs(text).format("DD/MM/YY")}</p>
          <p className="text-xs">{dayjs(text).format("dddd")}</p>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.entryDate, b.entryDate),
      },
    },
    {
      title: "Entry Price",
      dataIndex: "entryPrice",
      sorter: {
        compare: (a, b) => sortByNumber(a.entryPrice, b.entryPrice),
      },
    },
    {
      title: "Hit Bar",
      dataIndex: "hitDay",
      render: (text, obj) => {
        if (obj.incomplete) return "N/A";
        return text ? `Bar ${text}` : "Miss";
      },
      sorter: {
        compare: (a, b) => sortByNumber(a.hitDay, b.hitDay),
      },
    },
    {
      title: "Gain at exit",
      dataIndex: "hitDayReturn",
      render: (text, obj) => {
        if (obj.incomplete || text === null) return "—";
        return (
          <span className={returnUpDownClassname(text)}>{text}%</span>
        );
      },
      sorter: {
        compare: (a, b) => sortByNumber(a.hitDayReturn, b.hitDayReturn),
      },
    },
    {
      title: "EOD Peak",
      dataIndex: "eodPeakReturn",
      render: (text, obj) => (
        <span className={returnUpDownClassname(text)}>
          {text}% <span className="text-xs">(bar {obj.eodPeakDay})</span>
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.eodPeakReturn, b.eodPeakReturn),
      },
    },
    {
      title: "EOD Trough",
      dataIndex: "eodTroughReturn",
      render: (text, obj) => (
        <span className={returnUpDownClassname(text)}>
          {text}% <span className="text-xs">(bar {obj.eodTroughDay})</span>
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.eodTroughReturn, b.eodTroughReturn),
      },
    },
    {
      title: "Return at window end",
      dataIndex: "finalReturn",
      render: (text) => (
        <span className={returnUpDownClassname(text)}>{text}%</span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.finalReturn, b.finalReturn),
      },
    },
    {
      title: "Success",
      dataIndex: "success",
      render: (text, obj) =>
        obj.incomplete ? (
          <span>Incomplete</span>
        ) : (
          <span className={text ? "green" : "red"}>{text ? "Yes" : "No"}</span>
        ),
      filterMultiple: false,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => !record.incomplete && record.success === value,
    },
  ];

  return (
    <GenericCard
      title="Signals"
      className="table-card"
      isCardOpenByDefault={false}
      maxHeight={700}
    >
      <AntTable
        rowKey="entryDate"
        dataSource={sortedSignals}
        columns={columns}
      />
      <div className="pt-4">
        <ExportCsvButton
          data={sortedSignals}
          fileName="options-signal-analysis"
        />
      </div>
    </GenericCard>
  );
};

OptionsSignalTable.propTypes = {
  signals: PropTypes.array,
};

export default OptionsSignalTable;
